import React, { useState, useMemo } from "react";
import { View, Text, Pressable, ScrollView, StyleSheet } from "react-native";
import { MotiView, AnimatePresence } from "moti";
import { ArrowsClockwiseIcon, FileTextIcon, TrendUpIcon, CaretLeftIcon, BookOpenIcon } from "@phosphor-icons/react";

import AdminLayout from "../../layouts/AdminLayout.web";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  DataTable,
  CellTextBold,
  Column,
} from "../../components/adminDashboard/AICompanion";
import {
  colors,
  spacing,
  radii,
  typographyScale,
  typographyStyles,
  shadowStyles,
  webTransitions,
} from "../../styles/design-system";
import {
  useGetCourseSessionAnalyticsQuery,
  useGetUserSessionAnalyticsDailyBreakdownQuery,
  useGetUserSessionAnalyticsQuery,
  UserSessionAnalyticsInput,
  UserSessionAnalyticsResponse,
  CourseSessionAnalyticsInput,
} from "../../graphql/generated/graphql";
import { RobotIcon } from "@phosphor-icons/react";
import { useGetAdminDocumentAnalyticsQuery } from "../../graphql/generated/graphql";
import { formatTimeAgo, getLastNDaysDateRange } from "../../utils/date";
import AdminStatsCards from "../../components/dashboard/Admin/AdminStatsCards";

// Daily bar item with hover effects
interface DailyBarItemProps {
  day: string;
  sessions: number;
  maxSessions: number;
  index: number;
  onPress: () => void;
}

function DailyBarItem({ day, sessions, maxSessions, index, onPress }: DailyBarItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={barItemStyles.container}>
      <View style={[barItemStyles.background, isHovered && barItemStyles.backgroundHovered]}>
        <MotiView
          from={{ height: "0%" }}
          animate={{ height: `${(sessions / (maxSessions || 1)) * 100}%` }}
          // @ts-expect-error - transition type not fully typed
          transition={{ type: "timing", duration: 500, delay: index * 50 }}
          style={[barItemStyles.fill, isHovered && barItemStyles.fillHovered]}
        />
      </View>
      <View style={barItemStyles.label}>
        <Text style={[barItemStyles.dayText, isHovered && barItemStyles.dayTextHovered]}>{day}</Text>
        <Text style={barItemStyles.sessionsText}>{sessions.toLocaleString()}</Text>
      </View>
    </Pressable>
  );
}

const barItemStyles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    gap: spacing.xs,
    cursor: "pointer",
  },
  background: {
    width: "100%",
    height: 200,
    backgroundColor: colors.grey[100],
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: "hidden",
    justifyContent: "flex-end",
    ...webTransitions.fast,
  },
  backgroundHovered: {
    backgroundColor: colors.grey[200],
  },
  fill: {
    width: "100%",
    backgroundColor: colors.purple[500],
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    ...webTransitions.fast,
  },
  fillHovered: {
    backgroundColor: colors.purple[600],
  },
  label: {
    alignItems: "center",
  },
  dayText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[600],
    ...webTransitions.fast,
  },
  dayTextHovered: {
    color: colors.grey[900],
  },
  sessionsText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    color: colors.grey[400],
  },
});

function AICompanionDashboardView() {
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState("usage");
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(null);
  const [refreshRotation, setRefreshRotation] = useState(0);

  const dateRange = useMemo(() => getLastNDaysDateRange(7), []);

  // Fetch all-time aggregated stats
  const {
    data: analyticsData,
    loading: analyticsLoading,
    refetch: refetchAnalytics,
  } = useGetUserSessionAnalyticsQuery({
    variables: {
      input: {
        page: 1,
        pageSize: 0,
      } as UserSessionAnalyticsInput,
    },
  });

  // Fetch daily breakdown for last 7 days
  const {
    data: dailyBreakdownData,
    loading: dailyBreakdownLoading,
    refetch: refetchDailyBreakdown,
  } = useGetUserSessionAnalyticsDailyBreakdownQuery({
    variables: {
      input: {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      },
    },
  });

  const {
    data: documentAnalyticsData,
    loading: documentAnalyticsLoading,
    refetch: refetchDocumentAnalytics,
  } = useGetAdminDocumentAnalyticsQuery({
    variables: {
      input: null, // Use default date range (last 30 days)
    },
  });
  const aiStats = useMemo(() => {
    const response = analyticsData?.getUserSessionAnalytics as UserSessionAnalyticsResponse;
    const totalUsers = response?.totalUsers || 0;
    const totalSessions = response?.totalSessions || 0;
    const avgSessionLength = response?.avgSessionLength || 0;

    const formattedAvgLength = avgSessionLength > 0 ? `${avgSessionLength.toFixed(1)} min` : "0 min";

    return {
      totalUsers,
      // totalUsersChange: "",
      totalSessions,
      // totalSessionsChange: "",
      avgSessionLength: formattedAvgLength,
      // avgSessionLengthChange: "",
    };
  }, [analyticsData]);

  const dailyUsage = useMemo(() => {
    const breakdown = dailyBreakdownData?.getUserSessionAnalyticsDailyBreakdown?.data;
    if (breakdown && breakdown.length > 0) {
      return breakdown;
    }
    return [];
  }, [dailyBreakdownData]);

  const { data: courseAnalyticsData, refetch: refetchCourseAnalytics } = useGetCourseSessionAnalyticsQuery({
    variables: {
      input: {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        limit: 6,
      } as CourseSessionAnalyticsInput,
    },
  });

  const topCourses = useMemo((): Array<{
    id: string;
    name: string;
    code: string;
    sessions: number;
    students: number;
    instructor: string;
    trend: "up" | "down" | "stable";
  }> => {
    const courses = courseAnalyticsData?.getCourseSessionAnalytics?.data;
    if (courses && courses.length > 0) {
      return courses.map(
        (course: {
          id?: string | null;
          name?: string | null;
          course_code?: string | null;
          totalSessions?: number | null;
          students?: number | null;
          instructor?: string | null;
        }) => ({
          id: course.id || "",
          name: course.name || "",
          code: course.course_code || "",
          sessions: course.totalSessions || 0,
          students: course.students || 0,
          instructor: course.instructor || "N/A",
          trend: "stable" as "up" | "down" | "stable", // Skip for now
        })
      );
    }
    return [];
  }, [courseAnalyticsData]);

  const knowledgeBase = useMemo(() => {
    const analytics = documentAnalyticsData?.getAdminDocumentAnalytics?.data;
    if (analytics && analytics.length > 0) {
      return analytics.map((item: { document?: { title?: string | null }; referenceCount?: number | null }) => ({
        name: item.document?.title || "",
        queries: item.referenceCount || 0,
        helpful: 0,
        category: "",
      }));
    }
    return [];
  }, [documentAnalyticsData]);

  type Course = (typeof topCourses)[number];
  type KnowledgeBaseDoc = (typeof knowledgeBase)[number];

  const courseColumns: Column<Course>[] = [
    {
      key: "course",
      header: "Course",
      flex: 2,
      renderCell: (course) => (
        <View style={styles.courseCellContent}>
          <View style={styles.courseIcon}>
            <BookOpenIcon size={16} color={colors.purple[600]} weight="regular" />
          </View>
          <View>
            <Text style={styles.courseCode}>{course.code}</Text>
            <Text style={[styles.courseName, { maxWidth: 200 }]} numberOfLines={1} ellipsizeMode="tail">
              {course.name}
            </Text>
          </View>
        </View>
      ),
    },
    {
      key: "instructor",
      header: "Instructor",
      flex: 1.5,
      renderCell: (course) => course.instructor,
    },
    {
      key: "students",
      header: "Students",
      flex: 0.8,
      textAlign: "right",
      renderCell: (course) => course.students,
    },
    {
      key: "sessions",
      header: "Sessions",
      flex: 0.8,
      textAlign: "right",
      renderCell: (course) => <CellTextBold>{course.sessions.toLocaleString()}</CellTextBold>,
    },
    // Trend column hidden
    // {
    //   key: "trend",
    //   header: "Trend",
    //   flex: 0.5,
    //   textAlign: "right",
    //   alignItems: "flex-end",
    //   renderCell: (course) => (
    //     <>
    //       {course.trend === "up" && <TrendUp size={16} color={colors.success[500]} weight="fill" />}
    //       {course.trend === "down" && (
    //         <TrendUp size={16} color={colors.error[500]} weight="fill" style={{ transform: "rotate(180deg)" }} />
    //       )}
    //       {course.trend === "stable" && <View style={styles.stableTrend} />}
    //     </>
    //   ),
    // },
  ];

  const knowledgeBaseColumns: Column<KnowledgeBaseDoc>[] = [
    {
      key: "document",
      header: "Document",
      flex: 2,
      renderCell: (doc) => (
        <View style={styles.docCellContent}>
          <View style={styles.docIcon}>
            <FileTextIcon size={16} color={colors.purple[600]} weight="regular" />
          </View>
          <Text style={styles.docName}>{doc.name}</Text>
        </View>
      ),
    },
    // Category column hidden
    // {
    //   key: "category",
    //   header: "Category",
    //   flex: 1,
    //   renderCell: (doc) => (
    //     <View style={styles.categoryBadge}>
    //       <Text style={styles.categoryBadgeText}>{doc.category}</Text>
    //     </View>
    //   ),
    // },
    {
      key: "queries",
      header: "Queries",
      flex: 0.8,
      textAlign: "right",
      renderCell: (doc) => <CellTextBold>{doc.queries.toLocaleString()}</CellTextBold>,
    },
    // Helpful column hidden
    // {
    //   key: "helpful",
    //   header: "Helpful %",
    //   flex: 0.8,
    //   textAlign: "right",
    //   renderCell: (doc) => {
    //     const helpfulText = `${doc.helpful}%`;
    //     return (
    //       <CellTextBold style={doc.helpful >= 90 ? styles.textGreen : styles.textOrange}>{helpfulText}</CellTextBold>
    //     );
    //   },
    // },
  ];

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRefreshRotation((prev) => prev + 360);
    try {
      await Promise.all([
        refetchAnalytics(),
        refetchDailyBreakdown(),
        refetchDocumentAnalytics(),
        refetchCourseAnalytics(),
      ]);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error refreshing analytics:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const maxDailySessions = Math.max(...dailyUsage.map((d) => d.sessions));
  const selectedDay = selectedDayIndex !== null ? dailyUsage[selectedDayIndex] : null;
  const maxHourlySessions = selectedDay ? Math.max(...selectedDay.hourly) : 0;

  return (
    <AdminLayout withPadding={false}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.container}>
        {/* Page Header */}
        <MotiView
          from={{ opacity: 0, translateY: 20 }}
          animate={{ opacity: 1, translateY: 0 }}
          // @ts-expect-error - transition type not fully typed
          transition={{ type: "timing", duration: 500 }}
          style={styles.headerSection}>
          <View style={styles.headerContent}>
            <View>
              <View style={styles.titleRow}>
                <Text style={styles.title}>Dashboard</Text>
                <View style={styles.badge}>
                  <RobotIcon size={14} color={colors.purple[700]} weight="fill" />
                  <Text style={styles.badgeText}>AI Companion</Text>
                </View>
              </View>
              <Text style={styles.subtitle}>Monitor AI Companion usage across your institution.</Text>
            </View>
            <View style={styles.headerActions}>
              <View style={styles.lastUpdated}>
                <Text style={styles.lastUpdatedLabel}>Last Updated</Text>
                <Text style={styles.lastUpdatedValue}>{formatTimeAgo(lastUpdated)}</Text>
              </View>
              <Pressable
                onPress={handleRefresh}
                disabled={isRefreshing}
                style={[styles.refreshButton, isRefreshing && styles.refreshButtonDisabled]}
                aria-label="Refresh data">
                <MotiView
                  animate={{ rotate: `${refreshRotation}deg` }}
                  transition={{
                    rotate: {
                      type: "timing",
                      duration: 1000,
                    },
                  }}>
                  <ArrowsClockwiseIcon size={20} color={colors.grey[600]} weight="regular" />
                </MotiView>
              </Pressable>
            </View>
          </View>
        </MotiView>

        {/* Stats Grid - 3 cards */}

        <AdminStatsCards
          totalUsers={aiStats.totalUsers}
          totalSessions={aiStats.totalSessions}
          avgSessionLength={aiStats.avgSessionLength}
        />

        {/* Tabs: Usage Trends, Knowledge Base */}
        <MotiView
          from={{ opacity: 0, translateY: 20 }}
          animate={{ opacity: 1, translateY: 0 }}
          // @ts-expect-error - transition type not fully typed
          transition={{ type: "timing", duration: 500, delay: 150 }}>
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList variant="soft" style={styles.tabsList}>
              <TabsTrigger variant="soft" value="usage">
                <TrendUpIcon size={16} color={colors.grey[600]} weight="regular" />
                <Text style={styles.tabTriggerText}>Usage Trends</Text>
              </TabsTrigger>
              <TabsTrigger variant="soft" value="knowledge">
                <FileTextIcon size={16} color={colors.grey[600]} weight="regular" />
                <Text style={styles.tabTriggerText}>Knowledge Base</Text>
              </TabsTrigger>
            </TabsList>

            {/* Usage Trends Tab */}
            <TabsContent value="usage">
              <View style={styles.tabContentSpacing}>
                {/* Daily Usage Chart with Hourly Drilldown */}
                <View style={[styles.card, shadowStyles.sm]}>
                  <View style={styles.cardHeader}>
                    <View style={styles.cardHeaderLeft}>
                      {selectedDayIndex !== null && (
                        <Pressable
                          onPress={() => setSelectedDayIndex(null)}
                          style={styles.backButton}
                          aria-label="Go back">
                          <CaretLeftIcon size={16} color={colors.grey[600]} weight="regular" />
                          <Text style={styles.backButtonText}>Back</Text>
                        </Pressable>
                      )}
                      <Text style={styles.cardTitle}>
                        {selectedDayIndex !== null
                          ? `${selectedDay?.day}, ${selectedDay?.date} - Hourly Breakdown`
                          : "Daily Usage"}
                      </Text>
                    </View>
                    <Text style={styles.cardSubtitle}>{selectedDayIndex !== null ? "24 hours" : "Last 7 days"}</Text>
                  </View>

                  <AnimatePresence exitBeforeEnter>
                    {selectedDayIndex === null ? (
                      // Daily View
                      <MotiView
                        key="daily"
                        from={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        // @ts-expect-error - transition type not fully typed
                        transition={{ type: "timing", duration: 200 }}>
                        <View style={styles.chartContainer}>
                          {dailyUsage.map((day, index) => (
                            <DailyBarItem
                              key={day.day}
                              day={day.day}
                              sessions={day.sessions}
                              maxSessions={maxDailySessions}
                              index={index}
                              onPress={() => setSelectedDayIndex(index)}
                            />
                          ))}
                        </View>
                        <Text style={styles.chartHint}>Click on a day to see hourly breakdown</Text>
                      </MotiView>
                    ) : (
                      // Hourly View
                      <MotiView
                        key="hourly"
                        from={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        // @ts-expect-error - transition type not fully typed
                        transition={{ type: "timing", duration: 200 }}>
                        <View style={styles.hourlyChartContainer}>
                          {selectedDay?.hourly.map((sessions, hour) => (
                            <View key={hour} style={styles.hourlyBarContainer}>
                              <View style={styles.hourlyBarBackground}>
                                <MotiView
                                  from={{ height: "0%" }}
                                  animate={{ height: `${(sessions / (maxHourlySessions || 1)) * 100}%` }}
                                  // @ts-expect-error - transition type not fully typed
                                  transition={{ type: "timing", duration: 300, delay: hour * 20 }}
                                  style={styles.hourlyBarFill}
                                />
                              </View>
                            </View>
                          ))}
                        </View>
                        <View style={styles.hourlyLabels}>
                          <Text style={styles.hourlyLabel}>12am</Text>
                          <Text style={styles.hourlyLabel}>6am</Text>
                          <Text style={styles.hourlyLabel}>12pm</Text>
                          <Text style={styles.hourlyLabel}>6pm</Text>
                          <Text style={styles.hourlyLabel}>11pm</Text>
                        </View>
                        <View style={styles.hourlyStats}>
                          <Text style={styles.hourlyStatText}>
                            Total: <Text style={styles.hourlyStatValue}>{selectedDay?.sessions.toLocaleString()}</Text>{" "}
                            sessions
                          </Text>
                          <Text style={styles.hourlyStatText}>
                            Peak: <Text style={styles.hourlyStatValue}>{maxHourlySessions.toLocaleString()}</Text> at{" "}
                            {selectedDay?.hourly.indexOf(maxHourlySessions)}:00
                          </Text>
                        </View>
                      </MotiView>
                    )}
                  </AnimatePresence>
                </View>

                {/* Top Courses by Workspace Usage */}
                <View style={[styles.card, shadowStyles.sm]}>
                  <View style={styles.cardHeader}>
                    <Text style={styles.cardTitle}>Top Courses by AI Usage</Text>
                    <Text style={styles.cardSubtitle}>Last 7 days</Text>
                  </View>
                  <DataTable columns={courseColumns} data={topCourses} keyExtractor={(course) => course.id} />
                </View>
              </View>
            </TabsContent>

            {/* Knowledge Base Tab */}
            <TabsContent value="knowledge">
              <View style={[styles.card, shadowStyles.sm]}>
                <View style={styles.cardHeader}>
                  <Text style={styles.cardTitle}>Knowledge Base Performance</Text>
                  <Text style={styles.cardSubtitle}>Top documents by query volume</Text>
                </View>
                <DataTable columns={knowledgeBaseColumns} data={knowledgeBase} keyExtractor={(doc) => doc.name} />
              </View>
            </TabsContent>
          </Tabs>
        </MotiView>
      </ScrollView>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: colors.grey[50],
  },
  container: {
    maxWidth: 1280,
    alignSelf: "center",
    width: "100%",
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
    // @ts-expect-error - minHeight works on web
    minHeight: "100vh",
    userSelect: "text",
  },
  headerSection: {
    marginBottom: spacing.xl,
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: spacing.xs,
  },
  title: {
    fontSize: typographyScale.displayMd.fontSize,
    lineHeight: typographyScale.displayMd.lineHeight,
    fontWeight: typographyStyles.fontBold.fontWeight,
    color: colors.grey[900],
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: spacing.xxs,
    backgroundColor: colors.purple[100],
    borderRadius: radii.full,
  },
  badgeText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.purple[700],
  },
  subtitle: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    color: colors.grey[600],
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  lastUpdated: {
    alignItems: "flex-end",
  },
  lastUpdatedLabel: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  lastUpdatedValue: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[700],
  },
  refreshButton: {
    width: spacing["2xl"],
    height: spacing["2xl"],
    borderRadius: 20,
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderWidth: 1,
    borderColor: colors.grey[200],
    alignItems: "center",
    justifyContent: "center",
  },
  refreshButtonDisabled: {
    opacity: 0.5,
  },
  statsGrid: {
    flexDirection: "row",
    gap: spacing.lg,
    marginBottom: spacing.xl,
  },
  tabsList: {
    marginBottom: spacing.lg,
  },
  tabTriggerText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[600],
  },
  tabContentSpacing: {
    gap: spacing.xl,
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: spacing.lg,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing.lg,
  },
  cardHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  cardTitle: {
    fontSize: typographyScale.textMd.fontSize,
    lineHeight: typographyScale.textMd.lineHeight,
    fontWeight: typographyStyles.fontSemibold.fontWeight,
    color: colors.grey[900],
  },
  cardSubtitle: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    color: colors.grey[500],
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  backButtonText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[600],
  },
  chartContainer: {
    height: 256,
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
  },
  chartHint: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    color: colors.grey[500],
    textAlign: "center",
    marginTop: spacing.sm,
  },
  hourlyChartContainer: {
    height: 192,
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 2,
  },
  hourlyBarContainer: {
    flex: 1,
    height: 160,
  },
  hourlyBarBackground: {
    flex: 1,
    backgroundColor: colors.grey[100],
    borderTopLeftRadius: radii.xs,
    borderTopRightRadius: radii.xs,
    overflow: "hidden",
    justifyContent: "flex-end",
  },
  hourlyBarFill: {
    width: "100%",
    backgroundColor: colors.purple[500],
    borderTopLeftRadius: radii.xs,
    borderTopRightRadius: radii.xs,
  },
  hourlyLabels: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: spacing.xs,
    paddingHorizontal: spacing.xxs,
  },
  hourlyLabel: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    color: colors.grey[400],
  },
  hourlyStats: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
  },
  hourlyStatText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[600],
  },
  hourlyStatValue: {
    fontWeight: typographyStyles.fontSemibold.fontWeight,
    color: colors.grey[900],
  },
  courseCellContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  courseIcon: {
    width: spacing.xl,
    height: spacing.xl,
    borderRadius: radii.md,
    backgroundColor: colors.purple[50],
    alignItems: "center",
    justifyContent: "center",
  },
  courseCode: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[900],
  },
  courseName: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    color: colors.grey[500],
  },
  docCellContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  docIcon: {
    width: spacing.xl,
    height: spacing.xl,
    borderRadius: radii.md,
    backgroundColor: colors.purple[50],
    alignItems: "center",
    justifyContent: "center",
  },
  docName: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[900],
  },
});

export default AICompanionDashboardView;
